import axios from '../helpers/axios'
import { logError } from '../services';
import {
  ICreateMFAAppOptionRes,
  IMFAValidateRes,
  IMFAExistingPost,
  ILoginPost,
  IUser,
  IError,
  IResetPasswordPost,
  IImpersonatePerson,
  IImpersonateEntraUser,
  ILoginRes,
  IAuthUserMFAOptions,
  IDropdownResponse
} from '../models';
import { deleteCookie } from '../helpers';
/**
 * auths a user on login
 * @param data ILoginPost
 * @returns user or error
 */
export const login = async (data: ILoginPost): Promise<ILoginRes> => {
  try {
    const res = await axios.post('/api/authentication/login', data);
    return Promise.resolve(res.data);
  } catch (error) {
    logError(
      {
        payload: {
          userName: data.userName,
          url: error?.toJSON()?.config?.url,
          message: error?.toJSON()?.message
        }
      },
      'login failed'
    );
    return Promise.reject(error.response.data);
  }
};

export const logout = async (): Promise<any> => {
  try {
    const res = await axios.post('/api/authentication/logout');
    return Promise.resolve(res.data);
  } catch (error) {
    logError(
      {
        error
      },
      'logout failed'
    );
    return Promise.reject(error.response.data);
  }
};

/**
 * Send reset link
 * @param email string
 * @returns IForgotPasswordPost
 */
export const sendResetLink = async (data: { email: string }): Promise<boolean> => {
  try {
    const res = await axios.post('/api/authentication/forgot-password', data);
    return res.data;
  } catch (error) {
    logError(
      {
        error,
        payload: data
      },
      'sendResetLink failed'
    );
    return Promise.reject(error.response.data);
  }
};

/**
 * sets up a users password
 * @returns user or error
 */
export const resetPassword = async (data: IResetPasswordPost): Promise<IUser | IError> => {
  try {
    const { data: responseData } = await axios.post('/api/authentication/reset-password', data);
    return responseData;
  } catch (error) {
    logError(
      {
        payload: {
          email: data.email
        }
      },
      'resetPassword failed'
    );
    return Promise.reject(error.response.data);
  }
};

export const impersonateUser = async (personId: number): Promise<IImpersonatePerson> => {
  try {
    // remove this cookie whenever you impersonate so if you go to v1 it keeps context
    deleteCookie('ASP.NET_SessionId', '/', '.stonemartinbuilders.com');
    const { data } = await axios.post(`/api/authentication/impersonate/people/${personId}`);
    return data;
  } catch (error: any) {
    logError({ error, payload: { personId } }, 'impersonateUser failed');
    return Promise.reject(error.response.data);
  }
};

export const impersonateEntraUser = async (entraId: string): Promise<IImpersonateEntraUser> => {
  try {
    // remove this cookie whenever you impersonate so if you go to v1 it keeps context
    deleteCookie('ASP.NET_SessionId', '/', '.stonemartinbuilders.com');
    const { data } = await axios.post(`/api/authentication/impersonate/entra/${entraId}`);
    return data;
  } catch (error: any) {
    logError({ error, payload: { entraId } }, 'impersonateEntraUser failed');
    return Promise.reject(error.response.data);
  }
};

export const deimpersonateEntraUser = async (): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/authentication/de-impersonate`);
    return data;
  } catch (error: any) {
    logError({ error }, 'deimpersonateEntraUser failed');
    return Promise.reject(error.response.data);
  }
};

export const getUserMfaOptions = async (filters?: {
  isActive?: boolean;
  type?: string;
}): Promise<IAuthUserMFAOptions[]> => {
  try {
    const { data } = await axios.get(`/api/authentication/mfa/options`, {
      params: filters
    });
    return data;
  } catch (error: any) {
    logError({ error }, 'getUserMfaOptions failed');
    return Promise.reject(error.response.data);
  }
};

export const getUserMfaTypes = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/authentication/mfa/types`);
    return data;
  } catch (error: any) {
    logError({ error }, 'getUserMfaTypes failed');
    return Promise.reject(error.response.data);
  }
};

export const validateExistingMFA = async (postData: IMFAExistingPost): Promise<IMFAValidateRes> => {
  try {
    const { data } = await axios.post(`/api/authentication/mfa/validate`, postData);
    return data;
  } catch (error: any) {
    logError(
      {
        payload: {
          type: postData.type,
          twoFactorAuthenticationId: postData.twoFactorAuthenticationId
        }
      },
      'validateExistingMFA failed'
    );
    return Promise.reject(error.response.data);
  }
};

export const createMFAAppOption = async (name: string): Promise<ICreateMFAAppOptionRes> => {
  try {
    const { data } = await axios.post(`/api/authentication/mfa/options/app`, {
      displayName: name
    });
    return data;
  } catch (error: any) {
    logError({ error }, 'createMFAOption failed');
    return Promise.reject(error.response.data);
  }
};

export const createMFATextOption = async (phoneNumber: string): Promise<string> => {
  try {
    const { data } = await axios.post(`/api/authentication/mfa/options/text`, {
      phoneNumber
    });
    return data;
  } catch (error: any) {
    logError({ error }, 'createMFATextOption failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createMFAAuthyOption = async (phoneNumber: string): Promise<string> => {
  try {
    const { data } = await axios.post(`/api/authentication/mfa/options/authy`, { phoneNumber });
    return data;
  } catch (error: any) {
    logError({ error }, 'createMFAAuthyOption failed');
    return Promise.reject(error.response.data);
  }
};

export const deleteMFAOption = async (authId: string | number): Promise<any> => {
  try {
    const { data } = await axios.delete(`/api/authentication/mfa/${authId}`);
    return data;
  } catch (error: any) {
    logError({ error }, 'deleteMFAOption failed');
    return Promise.reject(error.response.data);
  }
};

export const validateNewMFA = async (
  authId: string | number,
  twoFactorCode: string
): Promise<IMFAValidateRes> => {
  try {
    const { data } = await axios.post(`/api/authentication/mfa/${authId}/validate`, {
      twoFactorCode
    });
    return data;
  } catch (error: any) {
    logError({ error }, 'validateNewMFA failed');
    return Promise.reject(error.response.data);
  }
};

export const sendSMSText = async (authId: string | number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/authentication/mfa/${authId}/send`);
    return data;
  } catch (error: any) {
    logError({ error }, 'sendSMSText failed');
    return Promise.reject(error.response.data);
  }
};

export const sendAuthyText = async (authId: string | number): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/authentication/mfa/${authId}/authy/send`);
    return data;
  } catch (error: any) {
    logError({ error }, 'sendAuthyText failed');
    return Promise.reject(error.response.data);
  }
};

export const validateResetPassword = async (post: {
  email: string;
  passwordToken: string;
}): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/authentication/validate-reset-password`, post);
    return data;
  } catch (error: any) {
    logError({ error }, 'validateResetPassword failed');
    return Promise.reject(error.response.data);
  }
};

export const entraLogin = async (authJwt: any): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/authentication/entra-login`, null, {headers:{Authorization: `Bearer ${authJwt}`}});
    return data;
  } catch (error: any) {
    logError({ error }, 'entraLogin failed');
    return Promise.reject(error.response.data);
  }
};

export const getLoginFlow = async (post: {
  email: string;
}): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/authentication/get-login-flow`, post);
    return data;
  } catch (error: any) {
    logError({ error }, 'getLoginFlow failed');
    return Promise.reject(error.response.data);
  }
};
